import { Pipe, PipeTransform } from '@angular/core';
import { DocumentSessionPartial } from '@domain';
import { DocumentItemTypeEnum } from 'src/app/domain/enums/documents/document-item-type.enum';
import { DocumentSessionTypeEnum } from 'src/app/domain/enums/documents/document-session-type.enum';

export interface ChecklistProgress {
    done: number;
    total: number;
    percent: number;
}

/**
 * Conta as unidades de checklist de uma seção (não-assinatura):
 * - Seção COM pastas (CHECKLIST_FOLDER): cada pasta é uma unidade (vazias contam).
 * - Seção SEM pastas: a própria seção é uma unidade (o check é manual).
 */
function sessionUnits(session: DocumentSessionPartial): { done: number; total: number } {
    const folders = (session.items ?? []).filter(
        item => item.type === DocumentItemTypeEnum.CHECKLIST_FOLDER
    );

    if (folders.length > 0) {
        return {
            total: folders.length,
            done: folders.filter(folder => folder.checklist?.done).length,
        };
    }

    return { total: 1, done: session.checklist?.done ? 1 : 0 };
}

/**
 * Calcula o progresso do checklist somando as unidades (pastas + seções sem pasta)
 * de TODO o documento. Seções de assinatura (SIGNATURE) ficam de fora.
 *
 * Ex.: 2 seções com 2 pastas cada = 4 unidades; marcar 1 pasta → 25%.
 */
export function computeChecklistProgress(
    sessions: DocumentSessionPartial[] | null | undefined
): ChecklistProgress {
    const list = (sessions ?? []).filter(s => s.type !== DocumentSessionTypeEnum.SIGNATURE);

    let done = 0;
    let total = 0;
    for (const session of list) {
        const units = sessionUnits(session);
        done += units.done;
        total += units.total;
    }

    const percent = total > 0 ? Math.round((done / total) * 100) : 0;
    return { done, total, percent };
}

/** Uma etapa da trilha do checklist: uma seção (fora assinatura), na ordem do documento. */
export interface ChecklistStep {
    sessionId: string;
    name: string;
    /** Posição na trilha, a partir de 1. */
    num: number;
    done: boolean;
    /** Primeira seção ainda pendente: a "próxima" etapa. */
    current: boolean;
    isFirst: boolean;
    isLast: boolean;
    /** A seção anterior está concluída (pinta o trecho da trilha acima do nó). */
    prevDone: boolean;
}

export interface ChecklistSteps {
    steps: ChecklistStep[];
    total: number;
    doneCount: number;
    current: ChecklistStep | null;
    complete: boolean;
}

/**
 * Lê as seções como etapas de uma trilha: cada seção não-assinatura é um passo e a
 * primeira pendente é a próxima. Diferente de `computeChecklistProgress`, que conta
 * pastas como unidades para o %, aqui a granularidade é a seção: é o que a trilha
 * da lista, a barra e o header mostram ("Etapa 2 de 3").
 */
export function computeChecklistSteps(
    sessions: DocumentSessionPartial[] | null | undefined
): ChecklistSteps {
    const list = (sessions ?? []).filter(s => s.type !== DocumentSessionTypeEnum.SIGNATURE);
    const firstPending = list.findIndex(s => !s.checklist?.done);

    const steps: ChecklistStep[] = list.map((session, index) => ({
        sessionId: session._id,
        name: session.name,
        num: index + 1,
        done: !!session.checklist?.done,
        current: index === firstPending,
        isFirst: index === 0,
        isLast: index === list.length - 1,
        prevDone: index > 0 && !!list[index - 1].checklist?.done,
    }));
    const doneCount = steps.filter(step => step.done).length;

    return {
        steps,
        total: steps.length,
        doneCount,
        current: steps[firstPending] ?? null,
        complete: steps.length > 0 && doneCount === steps.length,
    };
}

@Pipe({
    name: 'checklistCompletion',
    standalone: true,
})
export class ChecklistCompletionPipe implements PipeTransform {
    transform(sessions: DocumentSessionPartial[] | null | undefined): ChecklistProgress {
        return computeChecklistProgress(sessions);
    }
}
